import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Stack,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import styles from './Certifications.module.css';

const Certifications = () => {
  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState([]);

  // Delete confirm dialog state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, certType: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const formatApiError = (e) => {
    const status = e?.response?.status;
    const data = e?.response?.data;
    if (data?.error) return data.error;
    if (typeof data === 'string') return `${status ? `${status}: ` : ''}${data}`.trim();
    if (data) return `${status ? `${status}: ` : ''}${JSON.stringify(data)}`.trim();
    return e?.message || 'Unknown error';
  };

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    setIsLoading(true);
    setError(false);
    try {
      const resp = await axiosPrivate.get('/api/certification/certification-types');
      const rows = Array.isArray(resp?.data) ? resp.data : [];
      setAllData(rows);
      setFilteredData(rows);
      if (!silent) enqueueSnackbar('Certifications loaded', { variant: 'success' });
    } catch (e) {
      setError(true);
      if (!silent) enqueueSnackbar(formatApiError(e) || 'Failed to load certifications', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [axiosPrivate, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    const next = (allData || []).filter((c) => {
      const name = String(c?.Name ?? '').toLowerCase();
      const desc = String(c?.Description ?? '').toLowerCase();
      return name.includes(lower) || desc.includes(lower);
    });
    setFilteredData(next);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchTerm, allData]);

  const openDeleteDialog = (certType) => setDeleteDialog({ open: true, certType });
  const closeDeleteDialog = () => {
    if (isDeleting) return;
    setDeleteDialog({ open: false, certType: null });
  };

  const handleConfirmDelete = async () => {
    const certTypeId = deleteDialog?.certType?.CertificationTypeID;
    if (!certTypeId) return;
    setIsDeleting(true);
    try {
      await axiosPrivate.delete(`/api/certification/certification-types/${certTypeId}`);
      enqueueSnackbar('Certification type deleted', { variant: 'success' });
      setDeleteDialog({ open: false, certType: null });
      await fetchData({ silent: true });
    } catch (e) {
      enqueueSnackbar(formatApiError(e) || 'Failed to delete certification type', { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = useMemo(() => [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Action',
      width: 150,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      getActions: (params) => [
        <Tooltip key="view" title="View Certification" arrow placement="top">
          <span>
            <GridActionsCellItem
              icon={<ViewIcon />}
              label="View Certification"
              showInMenu={false}
              onClick={() => navigate(`/admin/certifications/edit/${params?.row?.CertificationTypeID}`)}
            />
          </span>
        </Tooltip>,
        <Tooltip key="edit" title="Edit Certification" arrow placement="top">
          <span>
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit Certification"
              showInMenu={false}
              onClick={() => navigate(`/admin/certifications/edit/${params?.row?.CertificationTypeID}`)}
            />
          </span>
        </Tooltip>,
        <Tooltip key="delete" title="Delete Certification" arrow placement="top">
          <span>
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete Certification"
              showInMenu={false}
              onClick={() => openDeleteDialog(params.row)}
            />
          </span>
        </Tooltip>,
      ]
    },
    {
      field: 'Name',
      headerName: 'Certification',
      minWidth: 300,
      flex: 1.5
    },
    {
      field: 'ActiveVolunteers',
      headerName: 'Active Volunteers',
      minWidth: 150,
      flex: 0.5,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const val = params.value ?? 0;
        return (
          <Typography variant="body2" sx={{ color: val > 0 ? 'success.main' : 'text.secondary', fontWeight: val > 0 ? 600 : 'normal' }}>
            {val}
          </Typography>
        );
      }
    },
    {
      field: 'InactiveVolunteers',
      headerName: 'Inactive Volunteers',
      minWidth: 160,
      flex: 0.5,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const val = params.value ?? 0;
        return (
          <Typography variant="body2" sx={{ color: val > 0 ? 'error.main' : 'text.secondary', fontWeight: val > 0 ? 600 : 'normal' }}>
            {val}
          </Typography>
        );
      }
    },
  ], [navigate]);

  // Loading state
  if (isLoading) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Manage Certifications</h1>
                <p>List of certifications available from Baptists on Mission</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <Box className={styles.loadingContainer}>
              <CircularProgress />
              <Typography className={styles.loadingText}>Loading certifications...</Typography>
            </Box>
          </div>
        </div>
      </main>
    );
  }

  // Error state
  if (error) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Manage Certifications</h1>
                <p>List of certifications available from Baptists on Mission</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <Box className={styles.errorContainer}>
              <Typography color="error">Error occurred while fetching certifications. Please contact an admin.</Typography>
            </Box>
          </div>
        </div>
      </main>
    );
  }

  // Custom pagination
  const totalCount = filteredData.length;
  const pageCount = Math.ceil(totalCount / paginationModel.pageSize) || 1;
  const startIndex = paginationModel.page * paginationModel.pageSize;
  const endIndex = startIndex + paginationModel.pageSize;
  // Sort the ENTIRE filtered list (not just the current page) before paginating.
  const sortedData = (() => {
    if (!sortModel.length) return filteredData;
    const { field, sort } = sortModel[0];
    if (!field || !sort) return filteredData;
    const dir = sort === 'desc' ? -1 : 1;
    return [...filteredData].sort((a, b) => {
      const av = String(a?.[field] ?? '').toLowerCase();
      const bv = String(b?.[field] ?? '').toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  })();
  const paginatedData = sortedData.slice(startIndex, endIndex);

  return (
    <main>
      {/* Blue Header Banner */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">Manage Certifications</h1>
              <p>List of certifications available from Baptists on Mission</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="full-row white">
        <div className="">
          <Box className={styles.container}>
            {/* Search and New Button Section */}
            <Paper className={styles.searchPaper}>
              <Box className={styles.searchContainer}>
                <Box className={styles.buttonContainer}>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/admin/certifications/new')}
                    startIcon={<AddIcon />}
                    className={styles.addButton}
                  >
                    New Certification
                  </Button>
                </Box>

                <TextField
                  className={styles.searchField}
                  label="Search Certifications"
                  placeholder="Search Certifications"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
            </Paper>

            {/* Data Table */}
            <Paper className={styles.dataGridPaper}>
              <DataGrid
                className="certificationsDataGrid"
                rows={paginatedData}
                columns={columns}
                getRowId={(row) => row.CertificationTypeID}
                disableRowSelectionOnClick
                autoHeight
                hideFooterPagination
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={(m) => {
                  setSortModel(m);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                components={{
                  NoRowsOverlay: () => (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4, gap: 0.5 }}>
                      <Typography sx={{ fontWeight: 600, color: '#21252C' }}>No certifications found</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Try a different search term.</Typography>
                    </Box>
                  ),
                }}
              />
            </Paper>

            {/* Custom Pagination */}
            <Paper className={styles.paginationPaper}>
              <Stack
                className={styles.paginationStack}
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{ width: '100%' }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {totalCount > 0 ? (paginationModel.page * paginationModel.pageSize) + 1 : 0} to{' '}
                  {Math.min((paginationModel.page + 1) * paginationModel.pageSize, totalCount)} of {totalCount} Certifications
                </Typography>

                <Stack className={styles.paginationControls} direction="row" alignItems="center">
                  <Pagination
                    count={pageCount}
                    page={paginationModel.page + 1}
                    onChange={(_, page) => setPaginationModel((prev) => ({ ...prev, page: page - 1 }))}
                    color="primary"
                    variant="outlined"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={1}
                    className="certificationsPagination"
                  />
                </Stack>

                <Box className={styles.pageSizeSelector}>
                  <Typography variant="body2" color="text.secondary">
                    Show:
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={paginationModel.pageSize}
                    onChange={(e) => {
                      const newLimit = parseInt(e.target.value);
                      setPaginationModel((prev) => ({ ...prev, pageSize: newLimit, page: 0 }));
                    }}
                    SelectProps={{ native: true }}
                    className={`${styles.pageSizeField} pageSizeField`}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </TextField>
                  <Typography variant="body2" color="text.secondary">
                    per page
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onClose={closeDeleteDialog}>
              <DialogTitle className={styles.dialogTitle}>Delete Certification</DialogTitle>
              <DialogContent>
                <Typography className={styles.dialogText}>
                  Are you sure you want to delete{' '}
                  <strong>{deleteDialog?.certType?.Name ?? 'this certification'}</strong>?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This will not remove existing volunteer certifications.
                </Typography>
              </DialogContent>
              <DialogActions className={styles.dialogActions}>
                <Button onClick={closeDeleteDialog} disabled={isDeleting} className={styles.cancelButton}>
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  variant="contained"
                  color="error"
                  className={styles.confirmButtonDelete}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </div>
      </div>
    </main>
  );
};

export default Certifications;
